import type { ApplicantSectionV2 } from "@credit-acceptance/step6-bff-client";
import { Loader2, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import cacMainLogo from "../../assets/images/cac_main_logo.jpg";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { getErrorMessage, isHttpError } from "../../lib/query/errors";
import type { BureauApplicantInput, BureauApplicantView, RepullTarget } from "./api/types";
import { useRepullBureau } from "./api/useRepullBureau";
import { useViewBureau } from "./api/useViewBureau";

export const VIEW_BUREAU_PANEL_ID = "view-bureau-panel";

interface ViewBureauModalProperties {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalId: string;
  /** Driver applicant section from the dashboard response — supplies name/SSN/applicantId. */
  driver: ApplicantSectionV2 | undefined;
  /** 2nd signer applicant section; undefined/`present: false` when the deal has no co-maker. */
  secondSigner: ApplicantSectionV2 | undefined;
  /** Dealer on the deal (dashboard `dealSummary.dealerId`); required by the repull endpoint. */
  dealerId: string | undefined;
  /** Whether the analyst may repull (e.g. false when the deal is read-only/locked). */
  canRepull: boolean;
}

interface ApplicantColumnProperties {
  label: string;
  applicant: BureauApplicantView;
}

/**
 * Builds the bureau applicant identity (name/SSN/id) from a dashboard
 * `ApplicantSectionV2` — the bureau endpoint itself doesn't return name or
 * SSN, only score/report/frozen data, so those come from the same dashboard
 * response the rest of the deal is already hydrated from.
 * @param {ApplicantSectionV2 | undefined} section - The driver or 2nd signer section.
 * @returns {BureauApplicantInput | undefined} The applicant identity, or undefined when absent/not present on the deal.
 */
function toApplicantInput(section: ApplicantSectionV2 | undefined): BureauApplicantInput | undefined {
  if (!section?.present || !section.applicantId) {
    return undefined;
  }
  const residence = section.residence;
  const name = [residence?.firstName, residence?.middleName, residence?.lastName, residence?.nameSuffix]
    .filter(Boolean)
    .join(" ");
  return {
    applicantId: section.applicantId,
    name,
    ssn: residence?.ssnOrItin ?? "",
  };
}

/**
 * One applicant's driver/2nd-signer column. SSN and the CAPS credit score
 * carry `fs-mask` so they're redacted the moment session-replay tooling
 * (FullStory, already a CAC dependency elsewhere) is bootstrapped for this
 * app — masking would otherwise need a follow-up pass once that lands.
 */
function ApplicantColumn({ label, applicant }: ApplicantColumnProperties) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-0.5 text-center text-xs">
      <div className="text-foreground font-semibold">{label}</div>
      <div className="font-bold">{applicant.name}</div>
      <div className="fs-mask">
        {t("viewBureau.ssn")}: <span className="fs-mask font-bold">{applicant.ssn}</span>
      </div>
      <div className="fs-mask">
        {t("viewBureau.capsCreditScore")}: <span className="fs-mask font-bold">{applicant.capsCreditScore ?? ""}</span>
      </div>
    </div>
  );
}

/**
 * View Bureau modal (OR-23155) — CAC/Internal Roles migration of the legacy
 * "View Bureaus: Step6 Actions" JSF popup. Phase 2 covers the read-only
 * header + driver/2nd-signer info panel; Phase 3 adds the report selector
 * and read-only content view; Phase 4 adds the repull workflow (OR-25237) —
 * a driver/2nd-signer target picker + Repull button, shown only when
 * `canRepull`. The repull call is synchronous and refreshes that applicant's
 * bureau view in place.
 * Phase 6: adds a footer `Close` button alongside the header X — both route
 * through `DialogClose`'s Radix `onOpenChange`.
 * Opened from the Actions FAB menu (see `ActionsPopover`), not its own FAB,
 * so unlike `AnalystNotesModal` this component has no trigger —
 * `open`/`onOpenChange` are fully controlled by the caller.
 */
function ViewBureauModal({
  open,
  onOpenChange,
  approvalId,
  driver,
  secondSigner,
  dealerId,
  canRepull,
}: ViewBureauModalProperties) {
  const { t } = useTranslation();
  const driverInput = toApplicantInput(driver);
  const secondSignerInput = toApplicantInput(secondSigner);
  const { data, isLoading, isError, error } = useViewBureau({
    approvalId,
    driver: driverInput ?? { applicantId: "", name: "", ssn: "" },
    secondSigner: secondSignerInput,
    enabled: open && !!driverInput,
  });
  const repullBureau = useRepullBureau();
  const [selectedReportId, setSelectedReportId] = useState("");
  const [repullTarget, setRepullTarget] = useState<RepullTarget>("driver");

  const repullApplicantId = repullTarget === "secondSigner" ? secondSignerInput?.applicantId : driverInput?.applicantId;
  const repullErrorMessage = isHttpError(repullBureau.error, 422)
    ? t("viewBureau.repullRefused")
    : getErrorMessage(repullBureau.error);

  function selectRepullTarget(target: RepullTarget) {
    setRepullTarget(target);
    repullBureau.reset();
  }

  function handleRepull() {
    if (!repullApplicantId || !dealerId) {
      return;
    }
    repullBureau.mutate({ approvalId, applicantId: repullApplicantId, dealerId });
  }

  const reports = data?.reports ?? [];
  const activeReportId = reports.some((report) => report.id === selectedReportId) ? selectedReportId : "";
  const selectedReport = reports.find((report) => report.id === activeReportId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        id={VIEW_BUREAU_PANEL_ID}
        showCloseButton={false}
        className="gap-0 overflow-hidden p-0 sm:max-w-3xl"
      >
        <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">{t("viewBureau.title")}</DialogTitle>
          <DialogDescription className="sr-only">{t("viewBureau.title")}</DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors"
            aria-label={t("viewBureau.close")}
          >
            <X className="size-4.5" aria-hidden="true" />
          </DialogClose>
        </div>

        <div className="flex flex-col gap-3 p-4">
          {isLoading && (
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              <Loader2 aria-hidden="true" className="size-3 animate-spin" />
              {t("viewBureau.loading")}
            </div>
          )}

          {isError && (
            <p role="alert" className="text-destructive text-xs">
              {t("viewBureau.loadError", { message: getErrorMessage(error) })}
            </p>
          )}

          {/* Dashboard data (which supplies driver/applicantId) hasn't
              resolved yet, so the bureau query is disabled — without this,
              the modal would silently render an empty body. */}
          {open && !driverInput && !isLoading && !isError && (
            <p className="text-muted-foreground text-xs">{t("viewBureau.applicantNotReady")}</p>
          )}

          {data && (
            <>
              <div className="text-center text-xs">
                <p className="font-bold">{t("viewBureau.disclosureIntro", { name: data.driver.name })}</p>
                <div className="mt-2 flex justify-center">
                  <img src={cacMainLogo} alt={t("viewBureau.logoAlt")} className="h-auto max-w-full" />
                </div>
                <p className="mt-2">{t("viewBureau.disclosureBody")}</p>
              </div>

              <div
                className={`border-border grid grid-cols-1 items-center gap-3 border p-2 ${
                  data.secondSigner ? "sm:grid-cols-3" : "sm:grid-cols-2"
                }`}
              >
                <div className="flex flex-col items-center gap-0.5 text-center text-xs">
                  <div>
                    {t("viewBureau.salesPerson")}: <span className="font-bold">{data.salesPerson}</span>
                  </div>
                  <div>
                    {t("viewBureau.approvalNumber")}: <span className="font-bold">{data.approvalNumber}</span>
                  </div>
                </div>
                <ApplicantColumn label={t("viewBureau.driver")} applicant={data.driver} />
                {data.secondSigner && (
                  <ApplicantColumn label={t("viewBureau.secondSigner")} applicant={data.secondSigner} />
                )}
              </div>

              <p className="text-muted-foreground text-xs">{t("viewBureau.pullInstructions")}</p>

              {canRepull && (
                <div className="flex flex-col gap-1">
                  <div className="flex flex-wrap items-center gap-3 text-xs">
                    <span className="font-semibold">{t("viewBureau.repullTargetLabel")}</span>
                    <label className="flex items-center gap-1">
                      <input
                        type="radio"
                        name="view-bureau-repull-target"
                        checked={repullTarget === "driver"}
                        onChange={() => selectRepullTarget("driver")}
                      />
                      {t("viewBureau.driver")}
                    </label>
                    {data.secondSigner && (
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          name="view-bureau-repull-target"
                          checked={repullTarget === "secondSigner"}
                          onChange={() => selectRepullTarget("secondSigner")}
                        />
                        {t("viewBureau.secondSigner")}
                      </label>
                    )}
                    <Button
                      type="button"
                      size="xs"
                      disabled={repullBureau.isPending || !repullApplicantId || !dealerId}
                      onClick={handleRepull}
                    >
                      {repullBureau.isPending ? t("viewBureau.repulling") : t("viewBureau.repullButton")}
                    </Button>
                  </div>

                  {repullBureau.isError && (
                    <p role="alert" className="text-destructive text-xs">
                      {t("viewBureau.repullError", { message: repullErrorMessage })}
                    </p>
                  )}

                  {repullBureau.data?.frozen && (
                    <p role="status" className="text-xs font-semibold text-amber-700">
                      {t("viewBureau.repullFrozen")}
                    </p>
                  )}
                </div>
              )}

              <div className="flex items-center gap-2">
                <label htmlFor="view-bureau-report-select" className="shrink-0 text-xs font-semibold">
                  {t("viewBureau.reportSelectLabel")}
                </label>
                <Select value={activeReportId} onValueChange={setSelectedReportId} disabled={reports.length === 0}>
                  <SelectTrigger id="view-bureau-report-select" className="h-7 w-48">
                    <SelectValue placeholder={t("viewBureau.reportSelectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {reports.map((report) => (
                      <SelectItem key={report.id} value={report.id}>
                        {report.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <Textarea
                readOnly
                value={selectedReport?.content ?? ""}
                aria-label={t("viewBureau.reportContentLabel")}
                className="fs-mask min-h-40"
              />
            </>
          )}
        </div>

        <DialogFooter className="border-border border-t px-4 py-2 sm:justify-center">
          <DialogClose asChild>
            <Button type="button" size="sm">
              {t("viewBureau.closeButton")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default ViewBureauModal;
